import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneIcon, EnvelopeIcon, GlobeAltIcon, HeartIcon } from '@heroicons/react/24/outline'
import { getTextareaClasses, labelClasses } from '../../styles/inputStyles'

const BookingStepConfirm = memo(function BookingStepConfirm({
  selectedOffering,
  schedulingType,
  scheduledDate,
  scheduledTime,
  contactHint,
  timePrefs,
  notes,
  onNotesChange,
  entityContact = null,
}) {
  const { t } = useTranslation()
  const hasVendorContact = entityContact && (entityContact.phone || entityContact.email || entityContact.website || entityContact.tipUrl)

  return (
    <div className="p-3 space-y-2.5">
      {/* Summary box */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
        <div className="flex justify-between items-start gap-2">
          <span className="text-gray-500 flex-shrink-0">{t('bookings.service', 'Service')}</span>
          <span className="font-medium text-gray-900 text-right">{selectedOffering?.name}</span>
        </div>
        {selectedOffering?.price && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('common.price', 'Price')}</span>
            <span className="font-semibold text-primary">${Number(selectedOffering.price).toFixed(2)}</span>
          </div>
        )}
        {schedulingType === 'single' && scheduledDate && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('bookings.date', 'Date')}</span>
            <span className="text-gray-900 text-right">{scheduledDate}{scheduledTime && ` at ${scheduledTime}`}</span>
          </div>
        )}
        {schedulingType === 'flexible' && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('bookings.schedule.flexible', 'Flexible')}</span>
            <span className="text-gray-900">{timePrefs.filter((p) => p.preferred_date).length} {t('bookings.options', 'options')}</span>
          </div>
        )}
        {contactHint && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('bookings.yourContact', 'Your contact')}</span>
            <span className="text-gray-900 text-right min-w-0 break-words">{contactHint}</span>
          </div>
        )}
      </div>

      <div>
        <label className={labelClasses}>{t('bookings.notes', 'Notes (optional)')}</label>
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={2}
          placeholder={t('bookings.notesPlaceholder', 'Any additional details for the vendor?')}
          className={getTextareaClasses(false)}
        />
      </div>

      {hasVendorContact && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1.5">{t('bookings.vendorContact', 'Reach the vendor')}</p>
          <div className="space-y-1.5">
            {entityContact.phone && (
              <a href={`tel:${entityContact.phone}`} className="flex items-center gap-1.5 text-xs text-gray-700 hover:text-primary transition-colors bg-gray-50 rounded-lg p-1.5">
                <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary" /><span>{entityContact.phone}</span>
              </a>
            )}
            {entityContact.email && (
              <a href={`mailto:${entityContact.email}`} className="flex items-center gap-1.5 text-xs text-gray-700 hover:text-primary transition-colors bg-gray-50 rounded-lg p-1.5">
                <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary" /><span className="break-all">{entityContact.email}</span>
              </a>
            )}
            {entityContact.website && (
              <a href={entityContact.website.startsWith('http') ? entityContact.website : `https://${entityContact.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-gray-700 hover:text-primary transition-colors bg-gray-50 rounded-lg p-1.5">
                <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary" /><span className="break-all">{entityContact.website}</span>
              </a>
            )}
            {entityContact.tipUrl && (
              <a href={entityContact.tipUrl.startsWith('http') ? entityContact.tipUrl : `https://${entityContact.tipUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-primary font-medium hover:text-primary/80 transition-colors bg-primary/10 border border-primary/20 rounded-lg p-1.5">
                <HeartIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary" /><span>{t('entities.entity.tipUrl', 'Support them')}</span>
              </a>
            )}
          </div>
        </div>
      )}
      {!hasVendorContact && (
        <p className="text-xs text-gray-400 leading-snug">{t('bookings.vendorContactFallback', 'Find the vendor’s contact details on their profile page.')}</p>
      )}

      <p className="text-xs text-gray-400 leading-snug">
        {t('bookings.offPlatformNotice', 'Coordinate service details directly with the vendor. TrckIt does not process payments or messages.')}
      </p>
    </div>
  )
})

export default BookingStepConfirm
